import api, { ApiResponse } from './api';

export interface UserRole {
  id: number;
  name: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  employeeId: number;
  isActive: boolean;
  isLocked: boolean;
  lockedUntil?: string;
  lastLoginDate?: string;
  createdDate: string;
  roles: UserRole[];
}

export interface UserCreateDto {
  username: string;
  email?: string;
  password: string;
  firstName?: string;
  lastName?: string;
  employeeId?: number;
  roleIds?: number[];
}

export interface UserUpdateDto {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  employeeId?: number;
  isActive?: boolean;
  roleIds?: number[];
}

export interface Role {
  id: number;
  name: string;
  description?: string;
}

const userService = {
  getAll: async (): Promise<ApiResponse<User[]>> => {
    return api.get('/users');
  },

  getById: async (id: number): Promise<ApiResponse<User>> => {
    return api.get(`/users/${id}`);
  },

  create: async (data: UserCreateDto): Promise<ApiResponse<{ id: number }>> => {
    return api.post('/users', data);
  },

  update: async (id: number, data: UserUpdateDto): Promise<ApiResponse<{ id: number }>> => {
    return api.put(`/users/${id}`, data);
  },

  delete: async (id: number): Promise<ApiResponse<{ id: number }>> => {
    return api.delete(`/users/${id}`);
  },

  resetPassword: async (id: number, newPassword: string): Promise<ApiResponse<null>> => {
    return api.post(`/users/${id}/reset-password`, { newPassword });
  },

  lockUser: async (id: number, lockedUntil?: string): Promise<ApiResponse<null>> => {
    return api.post(`/users/${id}/lock`, { lockedUntil });
  },

  unlockUser: async (id: number): Promise<ApiResponse<null>> => {
    return api.post(`/users/${id}/unlock`, {});
  },

  toggleStatus: async (id: number): Promise<ApiResponse<{ isActive: boolean }>> => {
    return api.post(`/users/${id}/toggle-status`, {});
  },

  getRoles: async (): Promise<ApiResponse<Role[]>> => {
    return api.get('/users/roles');
  },

  getUserRoles: async (id: number): Promise<ApiResponse<UserRole[]>> => {
    return api.get(`/users/${id}/roles`);
  },

  toggleNfc: async (id: number): Promise<ApiResponse<{ hasNfc: boolean }>> => {
    return api.post(`/users/${id}/toggle-nfc`, {});
  },
};

export default userService;
